import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import TurnstileCaptcha from '../components/TurnstileCaptcha';
import { 
  Shield, 
  ShieldCheck, 
  ShieldAlert, 
  Smartphone, 
  Monitor, 
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TestCaptcha = () => {
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false, site_key: '' });
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaStatus, setCaptchaStatus] = useState('idle'); // idle, loading, verified, error
  const [verifyResult, setVerifyResult] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const [deviceInfo, setDeviceInfo] = useState({});

  // Detect device info
  useEffect(() => {
    const ua = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const browser = ua.includes('Chrome') ? 'Chrome' : 
                   ua.includes('Safari') ? 'Safari' : 
                   ua.includes('Firefox') ? 'Firefox' : 
                   ua.includes('Edge') ? 'Edge' : 'Other';
    
    setDeviceInfo({
      isMobile,
      isIOS,
      isAndroid,
      browser,
      userAgent: ua.substring(0, 100) + '...'
    });
  }, []);

  // Fetch CAPTCHA config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/captcha/config`);
        if (res.ok) {
          const data = await res.json();
          setCaptchaConfig(data);
        }
      } catch (err) {
        console.error('Failed to fetch CAPTCHA config:', err);
      }
    };
    fetchConfig();
  }, []);

  // Handle CAPTCHA verification
  const handleVerify = (token) => {
    setCaptchaToken(token);
    setCaptchaStatus('verified');
    addToHistory('Token received', 'success', token.substring(0, 20) + '...');
  };

  const handleError = (error) => {
    setCaptchaStatus('error');
    addToHistory('CAPTCHA error', 'error', error);
  };

  const handleExpire = () => {
    setCaptchaToken('');
    setCaptchaStatus('idle');
    addToHistory('Token expired', 'warning', 'Please verify again');
  };

  // Add event to test history
  const addToHistory = (event, status, detail) => {
    setTestHistory(prev => [{
      time: new Date().toLocaleTimeString(),
      event,
      status,
      detail
    }, ...prev].slice(0, 10));
  };

  // Test token with backend
  const testTokenWithBackend = async () => {
    if (!captchaToken) {
      setVerifyResult({ success: false, message: 'No token to verify' });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/captcha/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: captchaToken, action: 'test' })
      });
      
      const data = await res.json();
      setVerifyResult({
        success: res.ok,
        message: data.message || data.detail,
        status: res.status
      });
      addToHistory('Backend verify', res.ok ? 'success' : 'error', data.message || data.detail);
    } catch (err) {
      setVerifyResult({ success: false, message: err.message });
      addToHistory('Backend verify', 'error', err.message);
    }
  };

  // Reset everything
  const resetTest = () => {
    setCaptchaToken('');
    setCaptchaStatus('idle');
    setVerifyResult(null);
    addToHistory('Reset', 'info', 'Test reset');
    // Force re-render of CAPTCHA component
    setCaptchaConfig(prev => ({ ...prev }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">CAPTCHA Test Page</h1>
          <p className="text-slate-600">Test Cloudflare Turnstile widget functionality</p>
        </div>

        {/* Device Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              {deviceInfo.isMobile ? <Smartphone className="w-5 h-5" /> : <Monitor className="w-5 h-5" />}
              Device Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Type:</span>
                <Badge className={`ml-2 ${deviceInfo.isMobile ? 'bg-orange-500' : 'bg-blue-500'}`}>
                  {deviceInfo.isMobile ? 'Mobile' : 'Desktop'}
                </Badge>
              </div>
              <div>
                <span className="text-slate-500">OS:</span>
                <span className="ml-2 font-medium">
                  {deviceInfo.isIOS ? 'iOS' : deviceInfo.isAndroid ? 'Android' : 'Desktop'}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Browser:</span>
                <span className="ml-2 font-medium">{deviceInfo.browser}</span>
              </div>
              <div>
                <span className="text-slate-500">CAPTCHA:</span>
                <Badge className={`ml-2 ${captchaConfig.enabled ? 'bg-green-500' : 'bg-red-500'}`}>
                  {captchaConfig.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CAPTCHA Widget Test */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              CAPTCHA Widget
            </CardTitle>
            <CardDescription>
              Site Key: {captchaConfig.site_key ? `${captchaConfig.site_key.substring(0, 20)}...` : 'Not configured'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status indicator */}
            <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
              {captchaStatus === 'idle' && <AlertCircle className="w-5 h-5 text-slate-400" />}
              {captchaStatus === 'verified' && <ShieldCheck className="w-5 h-5 text-green-500" />}
              {captchaStatus === 'error' && <ShieldAlert className="w-5 h-5 text-red-500" />}
              <span className="font-medium">
                Status: {captchaStatus === 'idle' ? 'Waiting for verification' : 
                        captchaStatus === 'verified' ? 'Verified!' : 'Error occurred'}
              </span>
            </div>

            {/* CAPTCHA Widget */}
            {captchaConfig.enabled && captchaConfig.site_key ? (
              <div className="flex justify-center p-4 bg-white rounded-lg border">
                <TurnstileCaptcha
                  siteKey={captchaConfig.site_key}
                  onVerify={handleVerify}
                  onError={handleError}
                  onExpire={handleExpire}
                  action="test"
                  theme="light"
                />
              </div>
            ) : (
              <div className="text-center p-8 bg-slate-100 rounded-lg">
                <ShieldAlert className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                <p className="text-slate-600">CAPTCHA not configured or disabled</p>
              </div>
            )}

            {/* Token display */}
            {captchaToken && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium mb-1">Token received:</p>
                <code className="text-xs text-green-700 break-all">
                  {captchaToken.substring(0, 50)}...
                </code>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button onClick={testTokenWithBackend} disabled={!captchaToken}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Verify with Backend
              </Button>
              <Button variant="outline" onClick={resetTest}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>

            {/* Backend verify result */}
            {verifyResult && (
              <div className={`p-3 rounded-lg ${verifyResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {verifyResult.success ? 
                    <CheckCircle className="w-5 h-5 text-green-600" /> : 
                    <XCircle className="w-5 h-5 text-red-600" />
                  }
                  <span className={`font-medium ${verifyResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {verifyResult.message}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Test History</CardTitle>
          </CardHeader>
          <CardContent>
            {testHistory.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No events yet. Interact with the CAPTCHA above.</p>
            ) : (
              <div className="space-y-2">
                {testHistory.map((item) => (
                  <div key={`${item.time}-${item.event}`} className="flex items-center gap-3 text-sm p-2 bg-slate-50 rounded">
                    <span className="text-slate-400 w-20">{item.time}</span>
                    <Badge className={
                      item.status === 'success' ? 'bg-green-500' :
                      item.status === 'error' ? 'bg-red-500' :
                      item.status === 'warning' ? 'bg-yellow-500' : 'bg-slate-500'
                    }>
                      {item.event}
                    </Badge>
                    <span className="text-slate-600 truncate">{item.detail}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Troubleshooting Tips */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Troubleshooting Tips</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-600 space-y-2">
            <p><strong>Widget not loading?</strong> Check if JavaScript is enabled and no ad blockers are active.</p>
            <p><strong>Verification failing?</strong> Try disabling VPN or switching to a different network.</p>
            <p><strong>On mobile?</strong> Try using the default browser (Safari on iOS, Chrome on Android).</p>
            <p><strong>Still issues?</strong> Clear browser cache and cookies, then reload.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TestCaptcha;
